/**
 * Content Template Service
 *
 * Handles API calls for AI-generated visual marketing content templates.
 * AI-powered image generation for marketing content.
 */

import axios from 'axios';
import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { baseAxiosConfig } from '../utils/apiConfig';

const contentAxios = axios.create(baseAxiosConfig);

/**
 * Content type display information
 */
export const CONTENT_TYPE_INFO = {
  gmb_post: {
    name: 'Google My Business Post',
    description: 'Professional images for GMB posts',
    icon: 'FaGoogle',
    color: 'blue',
  },
  website_hero: {
    name: 'Website Hero Banner',
    description: 'Full-width hero images for websites',
    icon: 'FaDesktop',
    color: 'purple',
  },
  about_page: {
    name: 'About Page Visual',
    description: 'Team and company imagery',
    icon: 'FaUsers',
    color: 'teal',
  },
  social_instagram: {
    name: 'Instagram Post',
    description: 'Square format social posts',
    icon: 'FaInstagram',
    color: 'pink',
  },
  social_facebook: {
    name: 'Facebook Post',
    description: 'Facebook-optimized visuals',
    icon: 'FaFacebook',
    color: 'facebook',
  },
  paid_ad_display: {
    name: 'Display Ad Creative',
    description: 'Banner ad templates',
    icon: 'FaBullhorn',
    color: 'orange',
  },
  paid_ad_social: {
    name: 'Social Ad Creative',
    description: 'Paid social campaign visuals',
    icon: 'FaAd',
    color: 'red',
  },
};

/**
 * Get all content types as an array
 */
export function getContentTypes() {
  return Object.entries(CONTENT_TYPE_INFO).map(([key, info]) => ({
    value: key,
    ...info,
  }));
}

/**
 * Get cached content templates for a vertical
 *
 * @param {string} vertical - Business vertical (e.g., 'junk_removal', 'hvac')
 * @param {string[]} contentTypes - Optional array of specific content types to fetch
 * @returns {Promise<Object>} Template set with templates array
 */
export async function getVerticalTemplates(vertical, contentTypes = null) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    const params = { user: user.email };

    if (contentTypes && contentTypes.length > 0) {
      params.content_types = contentTypes;
    }

    const response = await contentAxios.get(`/content-templates/templates/${vertical}`, {
      params,
      headers: { 'Authorization': `Bearer ${token}` },
    });

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      // No templates found - not an error, just empty
      return {
        vertical,
        templates: [],
        has_templates: false,
      };
    }
    logger.error("Error fetching content templates:", error);
    throw error;
  }
}

/**
 * Generate new content templates for a vertical
 *
 * @param {string} vertical - Business vertical
 * @param {string[]} contentTypes - Optional specific content types to generate
 * @param {Object} options - Additional options
 * @param {string} options.businessName - Business name to include in prompt
 * @param {string} options.customPrompt - Custom prompt to add to generation
 * @param {boolean} options.forceRegenerate - Force regeneration even if cached
 * @returns {Promise<Object>} Generation response with templates
 */
export async function generateTemplates(vertical, contentTypes = null, options = {}) {
  const { businessName, customPrompt, forceRegenerate = false } = options;

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const body = {
      vertical,
    };

    if (businessName) {
      body.business_name = businessName;
    }

    if (customPrompt) {
      body.custom_prompt = customPrompt;
    }

    if (contentTypes && contentTypes.length > 0) {
      body.content_types = contentTypes;
    }

    const response = await contentAxios.post('/content-templates/generate', body, {
      params: { user: user.email },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 300000, // 5 minute timeout for image generation
    });

    return response.data;
  } catch (error) {
    logger.error("Error generating content templates:", error);
    throw error;
  }
}

/**
 * Get list of available verticals with template status for current user
 *
 * @returns {Promise<Object[]>} Array of vertical info objects
 */
export async function getAvailableVerticals() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const response = await contentAxios.get('/content-templates/available-verticals', {
      params: { user: user.email },
    });
    return response.data;
  } catch (error) {
    logger.error("Error fetching available verticals:", error);
    throw error;
  }
}

/**
 * Delete cached templates for a vertical
 *
 * @param {string} vertical - Business vertical to delete templates for
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteVerticalTemplates(vertical) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await contentAxios.delete(`/content-templates/templates/${vertical}`, {
      params: { user: user.email },
      headers: { 'Authorization': `Bearer ${token}` },
    });

    return response.data;
  } catch (error) {
    logger.error("Error deleting content templates:", error);
    throw error;
  }
}

/**
 * Get content type information
 *
 * @returns {Promise<Object>} Content types info from API
 */
export async function getContentTypeInfo() {
  try {
    const response = await contentAxios.get('/content-templates/content-types');
    return response.data;
  } catch (error) {
    logger.error("Error fetching content type info:", error);
    // Fall back to local info
    return { content_types: getContentTypes() };
  }
}

/**
 * Map vertical from user profile to prompt vertical key
 *
 * @param {string} userVertical - Vertical from user profile
 * @returns {string} Mapped vertical key for prompts
 */
export function mapVerticalToPromptKey(userVertical) {
  if (!userVertical) return 'generic_business';

  const verticalLower = userVertical.toLowerCase().replace(/-/g, '_');

  // Direct mappings
  const mappings = {
    'hvac': 'hvac',
    'hotels': 'hotel',
    'hotel': 'hotel',
    'restaurants': 'restaurant',
    'restaurant': 'restaurant',
    'junk_removal': 'junk_removal',
    'junk-removal': 'junk_removal',
    'automotive': 'automotive',
    'auto': 'automotive',
    'healthcare': 'healthcare',
    'medical': 'healthcare',
    'retail': 'retail',
    'professional_services': 'professional_services',
  };

  // Check for direct mapping
  if (mappings[verticalLower]) {
    return mappings[verticalLower];
  }

  // Check for partial matches
  for (const [key, value] of Object.entries(mappings)) {
    if (verticalLower.includes(key)) {
      return value;
    }
  }

  return 'generic_business';
}

export default {
  getVerticalTemplates,
  generateTemplates,
  getAvailableVerticals,
  deleteVerticalTemplates,
  getContentTypeInfo,
  getContentTypes,
  mapVerticalToPromptKey,
  CONTENT_TYPE_INFO,
};
